import { NavLink, Outlet, useNavigate } from "react-router-dom";
import ReminderBell from "@/components/reminders/ReminderBell";
import NotificationBell from "@/components/nav/NotificationBell";
import RunningTimeHUD from "@/components/RunningTimeHUD";
import TenantSwitcher from "@/components/TenantSwitcher";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  LayoutDashboard,
  Users,
  Settings,
  Mail,
  Building2,
  LogOut,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Inbox,
  History as HistoryIcon,
  Clock,
  CalendarRange,
  History,
  ListTree,
  Timer,
  ClipboardList,
  Coffee,
  PalmtreeIcon,
  Scale,
  Tablet,
  KeyRound,
  Webhook,
  Sparkles,
  BarChart3,
  ScrollText,
  Plug,
  Bell,
  BellRing,
  MapPin,
  Upload,
  Download,
  DollarSign,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { usePaceStatus } from "@/hooks/use-pace-status";

const ALL = ["worker", "approver", "admin", "tenant_admin", "master"];

function buildSections({ isApprover, paceEnabled, isPlatformAdmin }) {
  return [
  {
    label: null,
    items: [
      { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ALL },
      ...(paceEnabled
        ? [{ to: "/app/pace", label: "Ask Pace", icon: Sparkles, roles: ALL }]
        : []),
    ],
  },
  {
    label: "My time",
    items: [
      { to: "/app/time", label: "Clock", icon: Clock, roles: ALL, exact: true },
      { to: "/app/time/week", label: "This week", icon: CalendarRange, roles: ALL },
      { to: "/app/time/history", label: "History", icon: History, roles: ALL },
      { to: "/app/tasks", label: "Tasks", icon: ClipboardList, roles: ALL },
      { to: "/app/reminders", label: "Reminders", icon: Bell, roles: ALL },
      { to: "/app/pto", label: "My PTO", icon: PalmtreeIcon, roles: ALL },
    ],
  },
  {
    label: "Approvals",
    show: isApprover || undefined,
    items: [
      { to: "/app/approvals", label: "Pending", icon: Inbox, roles: ALL, exact: true, showIf: "approver_or_admin" },
      { to: "/app/approvals/pto", label: "PTO queue", icon: PalmtreeIcon, roles: ALL, showIf: "approver_or_admin" },
      { to: "/app/approvals/history", label: "History", icon: HistoryIcon, roles: ALL, showIf: "approver_or_admin" },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/app/directory", label: "Directory", icon: Users, roles: ["admin", "tenant_admin", "master"] },
      { to: "/app/admin/users", label: "Team", icon: Shield, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/invites", label: "Invites", icon: Mail, roles: ["admin", "tenant_admin"] },
    ],
  },
  {
    label: "Workspace",
    items: [
      { to: "/app/admin/worksites", label: "Worksites", icon: Building2, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/dimensions", label: "Dimensions", icon: ListTree, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/approvals", label: "Approvals oversight", icon: ShieldCheck, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/overtime-policy", label: "Overtime rules", icon: Scale, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/rates", label: "Rates", icon: DollarSign, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/break-policy", label: "Meal-break rules", icon: Coffee, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/pto-types", label: "PTO types", icon: PalmtreeIcon, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/pto-requests", label: "PTO requests", icon: Inbox, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/checklists", label: "Checklists", icon: ClipboardList, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/tasks", label: "Scheduled tasks", icon: ClipboardList, roles: ["admin", "tenant_admin", "approver"] },
      { to: "/app/admin/reminders", label: "Timesheet reminders", icon: BellRing, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/kiosks", label: "Kiosks", icon: Tablet, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/privacy", label: "Privacy notice", icon: MapPin, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/api-keys", label: "API keys", icon: KeyRound, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/webhooks", label: "Webhooks", icon: Webhook, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/imports", label: "Bulk imports", icon: Upload, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/time-imports", label: "Time imports", icon: Upload, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/exports", label: "Bulk exports", icon: Download, roles: ["admin", "tenant_admin"] },
      { to: "/app/admin/integrations", label: "Integrations", icon: Plug, roles: ["tenant_admin"] },
      { to: "/app/admin/time-policy", label: "Time policy", icon: Timer, roles: ["tenant_admin"] },
      { to: "/app/admin/time", label: "Time browser", icon: ClipboardList, roles: ["admin", "tenant_admin"] },
    ],
  },
  {
    label: "Platform admin",
    items: [
      { to: "/master/tenants", label: "Workspaces", icon: Building2, roles: ["master"] },
      { to: "/master/api-usage", label: "API usage", icon: BarChart3, roles: ["master"] },
      // Phase 22 — cross-workspace surface for platform admins (identity flag).
      ...(isPlatformAdmin
        ? [
            { to: "/app/platform/tenants", label: "All workspaces", icon: Building2, roles: ["__pla__"], pla: true },
            { to: "/app/platform/admins", label: "Platform admins", icon: ShieldAlert, roles: ["__pla__"], pla: true },
            { to: "/app/platform/stats", label: "Platform stats", icon: BarChart3, roles: ["__pla__"], pla: true },
            { to: "/app/platform/impersonation-log", label: "View-as history", icon: ScrollText, roles: ["__pla__"], pla: true },
          ]
        : []),
    ],
  },
  {
    label: null,
    items: [
      { to: "/app/settings/profile", label: "Profile & MFA", icon: Settings, roles: ALL },
      { to: "/app/settings/notifications", label: "Notifications", icon: Bell, roles: ALL },
    ],
  },
  ];
}

export default function AppShell() {
  const { user, isApprover, isPlatformAdmin, hasRole, logout } = useAuth();
  const navigate = useNavigate();
  const { paceEnabled } = usePaceStatus();

  const initials = ((user?.full_name || user?.email || "?")
    .split(/\s+/)
    .map((s) => s[0])
    .join("")
    .slice(0, 2) || "?").toUpperCase();

  const isAdminish = hasRole(["admin", "tenant_admin"]);
  const sections = buildSections({ isApprover, paceEnabled, isPlatformAdmin });
  const visibleSections = sections
    .map((s) => {
      let items = s.items.filter((n) => n.pla ? isPlatformAdmin : hasRole(n.roles));
      // "Approvals" group visibility: only shown to explicit approvers OR admins
      if (s.label === "Approvals") {
        items = items.filter(() => isApprover || isAdminish);
      }
      return { ...s, items };
    })
    .filter((s) => s.items.length > 0);

  return (
    <div className="min-h-screen bg-background">
      <div className="grid min-h-screen grid-cols-[260px_1fr]">
        <aside className="brand-navy-gradient sticky top-0 flex h-screen flex-col text-white/90">
          <div className="px-6 py-6">
            <Logo variant="dark" size="sm" withTagline />
          </div>
          <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4">
            {visibleSections.map((section, idx) => (
              <div key={idx} className="space-y-1">
                {section.label && (
                  <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/40">
                    {section.label}
                  </div>
                )}
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    data-testid={`nav-${item.to.replace(/[\/]/g, "-").replace(/^-/, "")}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-white/10 text-white pill-glow"
                          : "text-white/70 hover:bg-white/5 hover:text-white"
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
          <div className="border-t border-white/10 p-4">
            <div className="mb-3 flex items-center gap-3">
              <Avatar className="h-9 w-9 border border-white/10">
                <AvatarFallback className="bg-secondary text-white">{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-white" data-testid="current-user-name">
                  {user?.full_name || user?.email}
                </div>
                <div className="truncate text-xs text-white/60">{user?.email}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {(user?.roles || []).map((r) => (
                <span key={r} className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wide text-white/80">
                  {r}
                </span>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 w-full justify-start gap-2 text-white/80 hover:bg-white/10 hover:text-white"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </aside>

        <main className="min-w-0 overflow-x-hidden">
          <ImpersonationBanner />
          <div className="flex items-center justify-end gap-3 px-8 pt-4">
            <TenantSwitcher />
            <NotificationBell />
            <ReminderBell />
          </div>
          <div className="mx-auto max-w-6xl px-8 pb-10 pt-4">
            <Outlet />
          </div>
        </main>
      </div>
      <RunningTimeHUD />
    </div>
  );
}
